import React from 'react';
import PropTypes from 'prop-types';
import {
    withStyles,
    Card,
    CardContent,
    Typography,
    Icon,
    Button
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { getAcademicYear } from '../helpers/date';

const styles = theme => ({
    root: {
        display: 'grid',
        gridGap: '20px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 0.5fr))',
        gridRowStart: '2',
        margin: '0 20px',
        justifyContent: 'space-evenly',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            margin: '0'
        }
    },
    passengerName: {
        marginBottom: '20px',
        '& > p': {
            fontSize: '16px'
        }
    },
    ticket: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        '&:last-child': {
            paddingBottom: '16px'
        },
        position: 'relative'
    },
    tripsLabel: {
        fontSize: '12px !important',
        color: 'green',
        textTransform: 'uppercase'
    },
    tripsLabelWarn: {
        color: '#848400'
    },
    tripsLabelFail: {
        color: 'red'
    },
    adhocExp: {
        color: 'red',
        fontSize: '13px !important'
    },
    tripsBadge: {
        background: 'green',
        color: 'white',
        borderRadius: '50%',
        padding: '3px',
        width: 20,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    tripsBadgeWarn: {
        background: '#e0e000',
        color: 'black'
    },
    tripsBadgeFail: {
        background: 'red',
        color: 'white'
    },
    ticketText: {
        flexGrow: 2
    },
    icon: {
        fontSize: '18px',
        marginLeft: '5px'
    },
    ticketName: {
        '& > h6': {
            fontSize: '1.1em'
        }
    },
    buyAgainButton: {
        marginLeft: '10px'
    },
    notifyBtn: {
        position: 'absolute',
        padding: 0,
        minWidth: '0',
        right: 10,
        border: 'none !important'
    },
    notifyIcon: {
        fontSize: '18px'
    }
});

const getTicketName = ticket => {
    const { type } = ticket;
    let formattedType = type.charAt(0).toUpperCase() + type.slice(1);

    formattedType = formattedType.replace('_', ' ');

    let description = `${ticket.service_name} ${formattedType}`;

    if (ticket.opt) {
        description += ` ${ticket.opt}`;
    }

    if (type === 'advance') {
        return description;
    }

    description += ` ${ticket.ticket_year}`;

    if (ticket.sub_type === 'pm_only') {
        description += ' (PM Only)';
    } else if (ticket.sub_type === 'am_only') {
        description += ' (AM Only)';
    } else if (ticket.sub_type === 'custom') {
        description += ' (Custom)';
    }

    return description;
};

const getPassengerName = ticket => {
    const { passenger } = ticket;

    return `${passenger.first_name} ${passenger.last_name}`;
};

const sortByDate = (a, b) => {
    if (a.date_purchased > b.date_purchased) {
        return -1;
    }
    if (a.date_purchased < b.date_purchased) {
        return 1;
    }
    return 0;
};

const hasExpired = ticket => {
    const now = new Date().toISOString();
    return ticket.expiry && ticket.expiry < now;
};

const getTripsLabelClassName = (ticket, classes) => {
    const tripsLeft = ticket.trips - ticket.trips_taken;
    let className = classes.tripsLabel;

    if (tripsLeft === 1 || tripsLeft === 2) {
        className += ` ${classes.tripsLabelWarn} trips-label-warn`;
    }

    if (tripsLeft < 1) {
        className += ` ${classes.tripsLabelFail} trips-label-fail`;
    }

    return className;
};

const getTripsBadgeClassName = (ticket, classes) => {
    const tripsLeft = ticket.trips - ticket.trips_taken;
    let className = classes.tripsBadge;

    if (tripsLeft === 1 || tripsLeft === 2) {
        className += ` ${classes.tripsBadgeWarn} trips-badge-warn`;
    }

    if (tripsLeft < 1) {
        className += ` ${classes.tripsBadgeFail} trips-badge-fail`;
    }

    return className;
};

const isAdhoc = ticket => {
    return (
        ticket.type === 'single' ||
        ticket.type === 'return' ||
        ticket.type === 'week' ||
        ticket.type === '4_week'
    );
};

const canBuyAgain = (ticket, currentAcademicYear) => {
    const hideBuyAgain = ['4338dc60-3dc5-45eb-98b5-426581ac1e17'];
    return (
        isAdhoc(ticket) &&
        ticket.sub_type !== 'fixed' &&
        ticket.ticket_year === currentAcademicYear &&
        !hideBuyAgain.includes(ticket.client_id)
    );
};

const TicketTable = ({ renewals, tickets, classes }) => {
    const academicYear = getAcademicYear();
    return (
        <div className={`${classes.root} TicketTable__root`}>
            {tickets.sort(sortByDate).map(ticket => {
                return (
                    <Card
                        key={ticket.id}
                        id={`ticket-${ticket.id}`}
                        className="TicketTable-ticket"
                    >
                        <CardContent className={classes.ticket}>
                            {ticket.notify && (
                                <Button
                                    className={classes.notifyBtn}
                                    variant="outlined"
                                    color="primary"
                                    component={Link}
                                    to={`/ticket/${ticket.id}/notifications`}
                                    style={{
                                        marginLeft: 10
                                    }}
                                >
                                    <Icon className={classes.notifyIcon}>
                                        notifications
                                    </Icon>
                                </Button>
                            )}
                            <div className={classes.ticketText}>
                                <div
                                    className={classes.ticketName}
                                    style={{
                                        paddingRight: ticket.notify
                                            ? '1rem'
                                            : '',
                                        paddingLeft: ticket.notify ? '1rem' : ''
                                    }}
                                >
                                    <Typography variant="h6" color="primary">
                                        {getTicketName(ticket)}
                                    </Typography>
                                </div>
                                <div className={classes.passengerName}>
                                    <Typography variant="body2">
                                        {getPassengerName(ticket)}
                                    </Typography>
                                    {ticket.type === 'flexi' && (
                                        <p
                                            className={`${getTripsLabelClassName(
                                                ticket,
                                                classes
                                            )} trips-label`}
                                        >
                                            <span
                                                className={`${getTripsBadgeClassName(
                                                    ticket,
                                                    classes
                                                )} trips-badge`}
                                            >
                                                {ticket.trips -
                                                    ticket.trips_taken}
                                            </span>{' '}
                                            trips left
                                        </p>
                                    )}
                                    {isAdhoc(ticket) && !hasExpired(ticket) && (
                                        <p className={classes.adhocExp}>
                                            Expires{' '}
                                            {moment(ticket.expiry).format(
                                                'ddd Do MMM HH:mm'
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className={classes.buttons}>
                                {!hasExpired(ticket) && (
                                    <>
                                        <Button
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            component={Link}
                                            to={`/ticket/${ticket.id}`}
                                        >
                                            Details
                                            <Icon className={classes.icon}>
                                                menu
                                            </Icon>
                                        </Button>
                                        {canBuyAgain(ticket, academicYear) && (
                                            <Button
                                                className={`${classes.button} ${classes.buyAgainButton}`}
                                                variant="outlined"
                                                color="primary"
                                                component={Link}
                                                to={`/buy-again/${ticket.ticket_year.replace(
                                                    '/',
                                                    '-'
                                                )}/${ticket.client_id}/${
                                                    ticket.service_id
                                                }/${ticket.ticket_id}/${
                                                    ticket.id
                                                }`}
                                            >
                                                Buy again
                                            </Button>
                                        )}
                                        {ticket.type === 'flexi' && (
                                            <Button
                                                className={`${classes.button} renew-btn`}
                                                variant="outlined"
                                                color="primary"
                                                component={Link}
                                                to={`/top-up/${ticket.id}`}
                                                style={{
                                                    marginLeft: 10,
                                                    background: '#1db91d',
                                                    color: 'white',
                                                    border: 'green'
                                                }}
                                            >
                                                Top up
                                                <Icon className={classes.icon}>
                                                    autorenew
                                                </Icon>
                                            </Button>
                                        )}
                                        {ticket.type === 'advance' && (
                                            <Button
                                                className={`${classes.button} renew-btn`}
                                                variant="outlined"
                                                color="primary"
                                                component={Link}
                                                to={`/book/${ticket.id}`}
                                                style={{
                                                    marginLeft: 10,
                                                    background: '#1db91d',
                                                    color: 'white',
                                                    border: 'green'
                                                }}
                                            >
                                                Book trip
                                            </Button>
                                        )}
                                    </>
                                )}
                                {hasExpired(ticket) && (
                                    <>
                                        <Button
                                            disabled
                                            className={classes.button}
                                            variant="outlined"
                                            color="primary"
                                            component={Link}
                                            to={`/ticket/${ticket.id}`}
                                        >
                                            Expired
                                        </Button>
                                        {canBuyAgain(ticket, academicYear) && (
                                            <Button
                                                className={`${classes.button} ${classes.buyAgainButton}`}
                                                variant="outlined"
                                                color="primary"
                                                component={Link}
                                                to={`/buy-again/${ticket.ticket_year.replace(
                                                    '/',
                                                    '-'
                                                )}/${ticket.client_id}/${
                                                    ticket.service_id
                                                }/${ticket.ticket_id}/${
                                                    ticket.id
                                                }`}
                                            >
                                                Buy again
                                            </Button>
                                        )}
                                    </>
                                )}
                                {renewals[ticket.id] && (
                                    <Button
                                        className={`${classes.button} renew-btn`}
                                        variant="outlined"
                                        color="primary"
                                        component={Link}
                                        to={`/renew/${renewals[ticket.id].id}`}
                                        style={{
                                            marginLeft: 10,
                                            background: '#1db91d',
                                            color: 'white',
                                            border: 'green'
                                        }}
                                    >
                                        Renew
                                        <Icon className={classes.icon}>
                                            autorenew
                                        </Icon>
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};

TicketTable.propTypes = {
    renewals: PropTypes.shape().isRequired,
    tickets: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            service_name: PropTypes.string,
            ticket_year: PropTypes.string,
            passenger: PropTypes.shape({
                first_name: PropTypes.string,
                last_name: PropTypes.string
            })
        })
    ).isRequired,
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(TicketTable);
